import { useEffect, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CheckIcon,
  ChevronDownIcon,
  GripVerticalIcon,
  PencilIcon,
  PlusIcon,
  SaveIcon,
  StarIcon,
  ThumbsUpIcon,
  Trash2Icon,
} from "lucide-react";
import {
  FieldArrayWithId,
  useFieldArray,
  UseFieldArrayAppend,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Column, ConfirmationDialog, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateSetJobInternalConfig } from "@/hooks/updateSets/useUpdateSetJobInternalConfig";
import {
  competencyRatingTypeOptions,
  evaluationBlockTypeOptions,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  EvaluationBuild,
  EvaluationBlockType,
  EvaluationScoringType,
  JobInternalConfig,
  EvaluationQuestionType,
} from "@/types";

// Zod schema for the form
const questionSchema = z.object({
  id: z.string(),
  question: z
    .string()
    .min(1, "Question is required. Delete if it's not needed."),
  required: z.boolean(),
  type: z.nativeEnum(EvaluationQuestionType).optional(),
});
const blockSchema = z.object({
  id: z.string(),
  blockType: z.nativeEnum(EvaluationBlockType, {
    message: "Block type is required",
  }),
  competency: z.string().min(1, "Competency is required"),
  description: z.string().min(1, "Description is required"),
  questions: z.array(questionSchema),
  scoringType: z.nativeEnum(EvaluationScoringType, {
    message: "Scoring type is required",
  }),
});
const evaluationBuildFormSchema = z.object({
  blocks: z.array(blockSchema),
  name: z.string().min(1, "Evaluation name is required"),
});

const defaultBlockValues = {
  id: crypto.randomUUID(),
  blockType: "",
  competency: "",
  description: "",
  questions: [],
  scoringType: "",
};

export const JobEvaluationBuild = ({
  className,
  evaluation,
  jobInternalConfig,
}: {
  className: string;
  evaluation: EvaluationBuild;
  jobInternalConfig: JobInternalConfig;
}) => {
  const [isReadOnly, setIsReadOnly] = useState(true);

  const form = useForm<z.infer<typeof evaluationBuildFormSchema>>({
    resolver: zodResolver(evaluationBuildFormSchema),
    defaultValues: {
      name: evaluation.name,
      blocks: evaluation.blocks,
    },
  });

  useEffect(() => {
    // Ensure blocks variable is updated whenever evaluation state changes.
    form.reset({
      name: evaluation.name,
      blocks: evaluation.blocks,
    });
  }, [evaluation, form]);

  const {
    fields: blocks,
    append,
    remove,
    move,
  } = useFieldArray({
    control: form.control,
    name: "blocks",
  });

  return isReadOnly ? (
    <ReadEvaluationBuild
      blocks={blocks}
      className={className}
      evaluation={evaluation}
      setIsReadOnly={setIsReadOnly}
    />
  ) : (
    <EditEvaluationBuild
      append={append}
      blocks={blocks}
      evaluation={evaluation}
      form={form}
      jobInternalConfig={jobInternalConfig}
      move={move}
      remove={remove}
      setIsReadOnly={setIsReadOnly}
    />
  );
};

const ReadEvaluationBuild = ({
  blocks,
  className,
  evaluation,
  setIsReadOnly,
}: {
  blocks: FieldArrayWithId<
    z.infer<typeof evaluationBuildFormSchema>,
    "blocks"
  >[];
  className: string;
  evaluation: EvaluationBuild;
  setIsReadOnly: (isReadOnly: boolean) => void;
}) => {
  const handleEdit = () => {
    setIsReadOnly(false);
  };

  return (
    <Column className={cn("gap-4", className)}>
      <Row className="justify-center gap-2">
        <Label className="text-lg font-semibold text-slate-700">
          {evaluation.name}
        </Label>
      </Row>
      {blocks.length > 0 ? (
        <Column className="gap-2">
          {blocks.map((block) => {
            return <ReadOnlyEvaluationBlock block={block} key={block.id} />;
          })}
        </Column>
      ) : (
        <Column className="items-center justify-center rounded-md border border-slate-200 p-8">
          <Label className="text-slate-500">
            Ruh Roh... No blocks have been added to this evaluation. Click edit
            to start building your evaluation!
          </Label>
        </Column>
      )}
      <Row className="items-center justify-end">
        <Button type="button" variant="outline" onClick={handleEdit}>
          <PencilIcon className="h-4 w-4" />
          Edit
        </Button>
      </Row>
    </Column>
  );
};

const EditEvaluationBuild = ({
  append,
  blocks,
  evaluation,
  form,
  jobInternalConfig,
  move,
  remove,
  setIsReadOnly,
}: {
  append: UseFieldArrayAppend<
    z.infer<typeof evaluationBuildFormSchema>,
    "blocks"
  >;
  blocks: FieldArrayWithId<
    z.infer<typeof evaluationBuildFormSchema>,
    "blocks"
  >[];
  evaluation: EvaluationBuild;
  form: UseFormReturn<z.infer<typeof evaluationBuildFormSchema>>;
  jobInternalConfig: JobInternalConfig;
  move: (oldIndex: number, newIndex: number) => void;
  remove: (index: number) => void;
  setIsReadOnly: (isReadOnly: boolean) => void;
}) => {
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] =
    useState(false);
  const { mutate: updateSetJobInternalConfig } =
    useUpdateSetJobInternalConfig();

  // Boiler plate for sorting using dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);

    move(oldIndex, newIndex);
  }

  const handleSubmit = (
    newEvaluation: z.infer<typeof evaluationBuildFormSchema>,
  ) => {
    if (!jobInternalConfig.evaluations) return;

    const evaluations = jobInternalConfig.evaluations.map((e) =>
      e.id === evaluation.id
        ? {
            ...e,
            name: newEvaluation.name,
            blocks: newEvaluation.blocks,
          }
        : e,
    );

    updateSetJobInternalConfig(
      {
        id: jobInternalConfig.id,
        updateRequest: {
          evaluations,
        },
      },
      {
        onSuccess: () => {
          setIsReadOnly(true);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!jobInternalConfig.evaluations) return;
    updateSetJobInternalConfig({
      id: jobInternalConfig.id,
      updateRequest: {
        evaluations: jobInternalConfig.evaluations.filter(
          (e) => e.id !== evaluation.id,
        ),
      },
    });
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4 p-6 pt-0"
        onSubmit={(e) => {
          void form.handleSubmit(handleSubmit)(e);
        }}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-600">Evaluation Name</FormLabel>
              <FormControl>
                <Input
                  className="h-9 border-none bg-slate-50 !text-xs"
                  placeholder="Evaluation Name"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {blocks.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={blocks.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              <Column className="gap-2">
                {blocks.map((block, index) => {
                  return (
                    <SortableBlock
                      block={block}
                      form={form}
                      index={index}
                      key={block.id}
                      remove={remove}
                    />
                  );
                })}
              </Column>
            </SortableContext>
          </DndContext>
        ) : null}

        <Button
          type="button"
          onClick={() => {
            // @ts-expect-error - purposefully sending in all blank values
            append(defaultBlockValues);
          }}
          variant="outlineDark"
          className="border-dashed border-slate-200 text-slate-600"
        >
          <PlusIcon className="h-4 w-4" />
          Add Block
        </Button>
        <Row className="items-center justify-between">
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setIsDeleteConfirmationDialogOpen(true);
            }}
          >
            <Trash2Icon className="h-4 w-4" />
            Delete
          </Button>
          <Button type="submit" variant="default">
            <SaveIcon className="h-4 w-4" />
            Save
          </Button>
        </Row>
      </form>
      <ConfirmationDialog
        confirmationButtonVariant="destructive"
        confirmButtonText="Delete"
        description="Are you sure you want to delete this ENTIRE evaluation? This action cannot be undone."
        onConfirm={handleDelete}
        onOpenChange={setIsDeleteConfirmationDialogOpen}
        open={isDeleteConfirmationDialogOpen}
        title="Delete Evaluation"
      />
    </Form>
  );
};

const ReadOnlyEvaluationBlock = ({
  block,
}: {
  block: FieldArrayWithId<z.infer<typeof evaluationBuildFormSchema>, "blocks">;
}) => {
  return (
    <Column className="gap-2 rounded-md border border-slate-200 p-4">
      <Row className="items-center justify-between">
        <Label className="text-md font-semibold text-slate-700">
          {block.competency}
        </Label>
        <Row className="items-center gap-2">
          <span className="rounded-lg border border-slate-300 px-3 text-xs text-slate-700">
            {block.blockType === EvaluationBlockType.OPENENDED
              ? "Open-ended"
              : "Structured"}
          </span>
          <Row className="items-center gap-1.5 rounded-lg bg-slate-50 p-2">
            {block.scoringType === EvaluationScoringType.RATING ? (
              <>
                <StarIcon className="h-3 w-3 text-slate-500" />
                <span className="text-xs text-slate-500">1-5</span>
              </>
            ) : block.scoringType === EvaluationScoringType.BINARY ? (
              <>
                <ThumbsUpIcon className="h-3 w-3 text-slate-500" />
                <span className="text-xs text-slate-500">Good/Bad</span>
              </>
            ) : (
              <>
                <CheckIcon className="h-3 w-3 text-slate-500" />
                <span className="text-xs text-slate-500">Completed</span>
              </>
            )}
          </Row>
        </Row>
      </Row>
      <Label className="text-sm text-slate-500">{block.description}</Label>
      <Collapsible
        className="group/collapsible rounded-lg bg-slate-50"
        defaultOpen
      >
        <Row className="items-center gap-2 text-slate-700">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="h-auto p-1 text-slate-500 hover:bg-transparent"
            >
              <ChevronDownIcon className="group-data-[state=open]/collapsible:rotate-180" />
            </Button>
          </CollapsibleTrigger>
          <Label className="text-sm font-normal text-slate-500">
            {block.blockType === EvaluationBlockType.OPENENDED
              ? "Guiding Questions"
              : "Structured Questions"}
          </Label>
          <span className="text-xs text-slate-500">
            ({block.questions.length})
          </span>
        </Row>
        <CollapsibleContent>
          <Column className="gap-2 rounded-lg bg-slate-50 p-2">
            {block.questions.map((question, i) => {
              const index = (i + 1).toString();
              return (
                <Row
                  className="ml-8 items-center gap-2 text-xs text-slate-500"
                  key={index}
                >
                  <span className="w-4">{index}.</span>
                  <Label className="font-normal" key={question.question}>
                    {question.question}
                  </Label>
                  {question.required && (
                    <span className="rounded-md border border-slate-500 px-1 text-xs text-slate-600">
                      Required
                    </span>
                  )}
                </Row>
              );
            })}
          </Column>
        </CollapsibleContent>
      </Collapsible>
    </Column>
  );
};

const SortableBlock = ({
  block,
  form,
  index,
  remove,
}: {
  block: FieldArrayWithId<z.infer<typeof evaluationBuildFormSchema>, "blocks">;
  form: UseFormReturn<z.infer<typeof evaluationBuildFormSchema>>;
  index: number;
  remove: (index: number) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-md border border-slate-200 p-3"
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="cursor-grab rounded p-2 hover:bg-slate-100 active:cursor-grabbing"
      >
        <GripVerticalIcon className="h-5 w-5 text-slate-400" />
      </button>
      <EditOnlyEvaluationBuildBlock form={form} index={index} />
      <Button
        className="text-slate-400 [&_svg]:size-5"
        onClick={() => {
          remove(index);
        }}
        size="icon"
        variant="ghost"
      >
        <Trash2Icon />
      </Button>
    </div>
  );
};

const EditOnlyEvaluationBuildBlock = ({
  form,
  index,
}: {
  form: UseFormReturn<z.infer<typeof evaluationBuildFormSchema>>;
  index: number;
}) => {
  const {
    fields: questions,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    name: `blocks.${index}.questions`,
  });

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const blockType = form.watch(`blocks.${index}.blockType`);
  const isStructured = blockType === EvaluationBlockType.STRUCTURED;

  return (
    <Column className="w-full gap-2">
      <FormField
        control={form.control}
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        name={`blocks.${index}.competency`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-normal text-slate-600">
              Competency
            </FormLabel>
            <FormControl>
              <Input
                className="h-9 border-none bg-slate-50 !text-xs text-slate-700"
                placeholder="e.g., Energy, Manual Labor Ability"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        name={`blocks.${index}.description`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-normal text-slate-600">
              Description
            </FormLabel>
            <FormControl>
              <Textarea
                className="border-none bg-slate-50 !text-xs text-slate-700"
                placeholder="e.g., The hiring manager will immediately cut those that look sluggish and disinterested."
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        name={`blocks.${index}.blockType`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-normal text-slate-600">
              Block Type
            </FormLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl>
                <SelectTrigger className="h-9 border-none bg-slate-50 p-2 text-xs text-slate-700">
                  <SelectValue placeholder="Select Block Type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {evaluationBlockTypeOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-xs text-slate-700"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        name={`blocks.${index}.scoringType`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-normal text-slate-600">
              Scoring Type
            </FormLabel>
            <Select
              defaultValue={field.value as EvaluationScoringType}
              onValueChange={field.onChange}
            >
              <SelectTrigger className="h-9 border-none bg-slate-50 p-2 text-xs text-slate-700">
                <SelectValue placeholder="Select Scoring Type" />
              </SelectTrigger>
              <SelectContent>
                {competencyRatingTypeOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-xs text-slate-700"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <div>
        <Label className="font-normal text-slate-600">
          {isStructured ? "Structured Questions" : "Guiding Questions"}
        </Label>
        {questions.length > 0 ? (
          <Column className="gap-2 pl-4 pt-4">
            {questions.map((question, i) => {
              return (
                <Row className="items-center gap-2" key={question.id}>
                  <FormField
                    control={form.control}
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    name={`blocks.${index}.questions.${i}.question`}
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControl>
                          <Row className="items-center gap-2">
                            <span className="w-4 text-sm text-slate-400">
                              {i + 1}.
                            </span>
                            <Input
                              className="h-9 border-none bg-slate-50 !text-xs text-slate-700"
                              placeholder="e.g., What has been your favorite job?"
                              {...field}
                            />
                          </Row>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    name={`blocks.${index}.questions.${i}.required`}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Button
                            type="button"
                            className={cn(
                              "h-8 min-w-20 px-2 py-0 text-xs transition-colors",
                              field.value
                                ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                : "border-slate-200 text-slate-500 hover:bg-slate-50",
                            )}
                            onClick={() => {
                              field.onChange(!field.value);
                            }}
                            variant="outlineDark"
                          >
                            {field.value ? "Required" : "Optional"}
                          </Button>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    className="text-slate-400"
                    onClick={() => {
                      remove(i);
                    }}
                    size="icon"
                    variant="ghost"
                  >
                    <Trash2Icon className="h-4 w-4" />
                  </Button>
                </Row>
              );
            })}
          </Column>
        ) : (
          <p className="py-4 text-center text-slate-400">
            {`No ${isStructured ? "structured" : "guiding"} questions have been created`}
          </p>
        )}
        <Button
          className="mt-1 px-2 text-xs font-normal text-slate-500"
          onClick={() => {
            append({
              id: crypto.randomUUID(),
              question: "",
              required: isStructured,
              type: isStructured ? EvaluationQuestionType.BINARY : undefined,
            });
          }}
          type="button"
          variant="ghost"
        >
          <PlusIcon className="h-4 w-4" />
          Create question
        </Button>
      </div>
    </Column>
  );
};
